import { Box, Typography } from '@mui/material';
import { DatePicker } from '../../../../lib/components';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DoughnutChart, RoundedPaper, StatusBar } from '../../../../lib/components';
import { format } from 'date-fns';
import Util from '../../../../misc/util';

type Props = {
  total_available_amount?: number;
  total_used_amount?: number;
  onDateChanged?: (date: Date) => void;
  initialDate?: Date;
  size?: 'small' | 'large';
};

export const ActiveBudget: FC<Props> = ({
  total_available_amount,
  total_used_amount,
  onDateChanged,
  initialDate,
  size = 'large'
}) => {
  const { t } = useTranslation();
  const showDatePicker = onDateChanged ?? false;
  initialDate = initialDate ? Util.getLastDayOfMonth(initialDate) : Util.getLastDayOfMonth();

  const available_amount = total_available_amount ?? 0;
  const used_amount = total_used_amount ?? 0;
  const open_amount = available_amount - used_amount;
  const used_prc =
    used_amount > 0 && available_amount > 0
      ? Math.round((used_amount / available_amount) * 100)
      : 0;

  const data = [
    { name: t('common:OPEN_adverb'), value: open_amount },
    { name: t('common:USED'), value: used_amount }
  ];

  return (
    <RoundedPaper sx={{ padding: '24px', gridColumn: size == 'large' ? '1 / 3' : '1 / 2' }}>
      <Typography variant="h3" marginBottom="16px" sx={{ float: 'left' }}>
        {t('employee:ACTIVE_BUDGET')}
      </Typography>
      {showDatePicker && (
        <Box
          sx={{
            float: 'right',
            '& .MuiInput-root': {
              maxWidth: size == 'small' ? '114px' : '184px',
              height: '32px'
            },
            '& .MuiInputAdornment-root': { width: 0 },
            '& .MuiButtonBase-root': { marginLeft: '-32px' }
          }}
        >
          <DatePicker
            initialValue={format(initialDate, 'yyyy-MM-dd')}
            onChange={() => {
              undefined;
            }}
            onAccept={(value) => {
              value && onDateChanged && onDateChanged(value);
            }}
            onChangeInvalidDate={(error) => {
              console.error(error);
            }}
            variant="month"
            size={size == 'large' ? 'normal' : 'small'}
          />
        </Box>
      )}
      <Box sx={{ clear: 'both' }} />
      {size == 'large' ? (
        <>
          <Box paddingBottom="16px" sx={{ clear: 'both' }}>
            <Typography
              display="inline"
              variant="h1"
              fontWeight="bold"
              marginRight="40px"
              lineHeight="40px"
            >{`${Util.formatCurrency(available_amount)}`}</Typography>
            <Box
              lineHeight="40px"
              display="inline"
              marginRight="40px"
              sx={{ verticalAlign: 'super' }}
            >
              <Box
                sx={{
                  height: '20px',
                  width: '20px',
                  backgroundColor: 'heaven.main',
                  display: 'inline-block',
                  borderRadius: '10px',
                  marginRight: '8px',
                  verticalAlign: 'sub'
                }}
              />
              <Typography display="inline" lineHeight="inherit">
                {`${Util.formatCurrency(used_amount)} ${t('common:USED')}`}
              </Typography>
            </Box>
            <Box lineHeight="40px" display="inline" sx={{ verticalAlign: 'super' }}>
              <Box
                sx={{
                  height: '20px',
                  width: '20px',
                  backgroundColor: 'mint.main',
                  display: 'inline-block',
                  borderRadius: '10px',
                  marginRight: '8px',
                  verticalAlign: 'sub'
                }}
              />
              <Typography display="inline" lineHeight="inherit">
                {`${Util.formatCurrency(open_amount)} ${t('common:OPEN_adverb')}`}
              </Typography>
            </Box>
          </Box>
          <StatusBar
            value={used_prc}
            displayMode="both"
            height={32}
            borderRadius={16}
            collapseRate={0.625}
            collapsable={true}
          />
        </>
      ) : (
        <DoughnutChart
          startAngle={180}
          endAngle={-180}
          data={data}
          variant="overlapping"
          withTooltip
          buildTooltipText={(value) => `${value} €`}
          withLegend
          legendHeadline={Util.formatCurrency(available_amount)}
          formatLegendValue={(v: number) => Util.formatCurrency(v)}
        />
      )}
    </RoundedPaper>
  );
};
